import { exportFile } from "@/lib/exportFile";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { CloudCheck, Download, Upload } from "lucide-react";
import { useData } from "@/context/DataContext";
import { api, errMsg } from "@/lib/api";
import { today } from "@/lib/money";
import { t } from "@/lib/i18n";
import { backupBlockReason } from "@/lib/backupGuard";
import { connectionOnline, nativeOffline, offline } from "@/lib/offlineNative";

export default function Backup() {
  const { load } = useData();
  const ref = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const exp = async () => {
    setExporting(true);
    try {
      if (nativeOffline) {
        if (!connectionOnline()) throw new Error(backupBlockReason({ online:false }));
        await offline.sync(true);
        const reason = backupBlockReason({
          online:connectionOnline(), pending:offline.state.queue.length, busy:!!offline.running,
          error:offline.state.failure, needsRefresh:offline.state.needsRefresh,
        });
        if (reason) throw new Error(reason);
      }
      const { data } = await api.get("/export");
      if (nativeOffline) {
        const reason = backupBlockReason({
          online:connectionOnline(), pending:offline.state.queue.length, busy:!!offline.running,
          error:offline.state.failure, needsRefresh:offline.state.needsRefresh,
        });
        if (reason) throw new Error(reason);
      }
      await exportFile(new Blob([JSON.stringify(data, null, 1)], { type: "application/json" }), `budgetpulse-backup-${today()}.json`);
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const imp = async (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const { data } = await api.post("/import", await f.text(), { headers: { "Content-Type": "application/json" } });
      const n = Object.values(data.imported).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${n} new record${n === 1 ? "" : "s"}. Duplicates were skipped.`);
      await load();
    } catch (x) { toast.error(errMsg(x)); } finally { setImporting(false); }
  };

  return (
    <section className="mm-card mm-rise" data-testid="backup-card">
      <h2 className="mm-h2 mb-1 text-lg">{t("Backup & restore")}</h2>
      <p className="mb-4 flex items-center gap-2 text-sm text-[var(--mu)]"><CloudCheck className="h-4 w-4 text-[var(--ok)]" /> {t("Saved to your account automatically. Restore also accepts backups from Version 2.2.")}</p>
      <div className="flex flex-wrap gap-2">
        <button className="mm-btn ghost" onClick={exp} disabled={exporting || importing} data-testid="backup-json-export-button"><Download className="h-4 w-4" /> {exporting ? 'Checking sync…' : t("Backup")}</button>
        <button className="mm-btn ghost" onClick={() => ref.current.click()} disabled={exporting || importing} data-testid="restore-json-import-button"><Upload className="h-4 w-4" /> {importing ? t("Restoring…") : t("Restore")}</button>
        <input ref={ref} type="file" accept="application/json,.json" hidden onChange={imp} data-testid="restore-file-input" />
      </div>
    </section>
  );
}
